import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    componentAutoReaction,
    deepStringFind,
    inject,
    ModalHandle,
    NothingHere,
    TableHeadOptions,
    SubTopBar,
    Title,
    toArray,
    toKeyArray,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsPeopleFill } from '@react-icons/all-files/bs/BsPeopleFill';
import { RouteComponentProps } from 'react-router';
import { Input as RInput } from 'reactstrap';
import { AccountStore } from '../Account/AccountStore';
import { AccountsStore } from '../Accounts/AccountsStore';
import { AppStore } from '../Core/AppStore';
import { InviteModal } from '../Invites/InviteModal';
import { UserCard } from './UserCard';
import { UserListViewSelector, UserListViewTypes } from './UserListViewSelector';
import { UserRoleViews, UserRoleViewSelector } from './UserRoleViewSelector';
import { UserRow } from './UserRow';
import { UsersStore } from './UsersStore';

export interface IUserListParams {
    readonly userRoleView: UserRoleViews;
}

export interface IUsersListProps extends RouteComponentProps<IUserListParams> {}

@autoinject
@observer
export class UsersList extends React.Component<IUsersListProps> {
    public componentDidMount() {
        componentAutoReaction(this, () => this.props.match.params.userRoleView, this.loadDataAsync);
    }

    public render() {
        const userRoleView = this.props.match.params.userRoleView;
        return (
            <div className="container-fluid">
                <Title>Users</Title>

                <SubTopBar>
                    <RInput
                        type="text"
                        placeholder="Search Users..."
                        value={this.searchValue}
                        onChange={this.onSearchInputChange}
                        className="mw-450"
                    />

                    {this.accountStore.isInRoles(['MriAdmin']) && (
                        <UserRoleViewSelector className="ms-3" baseUrl="/users" userRoleView={userRoleView} />
                    )}

                    <UserListViewSelector className="ms-3" />

                    <button className="btn btn-primary ms-auto" onClick={this.openModal}>
                        Invite User
                    </button>
                </SubTopBar>

                <div className="row">
                    <div className="col-12 p-0">
                        {!this.appStore.isMobile && (
                            <table className="table-striped w-100">
                                <thead className="bg-color">
                                    <tr>
                                        {this.usersStore.viewType == UserListViewTypes.UserView && (
                                            <th className="ps-2 py-3">
                                                User Name
                                                <TableHeadOptions
                                                    value={this.sort}
                                                    onChange={this.onSortChange}
                                                    field="userName"
                                                    exclusive
                                                />
                                            </th>
                                        )}

                                        {this.usersStore.viewType == UserListViewTypes.AccountView && (
                                            <th className="ps-2 py-3">Account/User Name</th>
                                        )}
                                        <th>
                                            Email
                                            <TableHeadOptions
                                                value={this.sort}
                                                onChange={this.onSortChange}
                                                field="email"
                                                exclusive
                                            />
                                        </th>
                                        <th>Roles</th>
                                        <th>Accounts</th>
                                        {this.accountStore.isInRoles(['MriAdmin']) && <th>Subscribed to Alerts</th>}
                                        <th>Actions</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {this.usersStore.viewType == UserListViewTypes.UserView &&
                                        this.users.map(u => <UserRow key={u.id} user={u} />)}

                                    {this.usersStore.viewType == UserListViewTypes.AccountView &&
                                        toKeyArray(this.accountUsers).map(a => (
                                            <>
                                                <tr className="user-list-account-row">
                                                    <td className="px-2" colSpan={5}>
                                                        <b>
                                                            {a.startsWith('__')
                                                                ? a.substring(2)
                                                                : this.accountsStore.accounts.get(a)?.accountName ??
                                                                  'No Assigned Accounts'}
                                                        </b>
                                                    </td>
                                                </tr>
                                                {this.accountUsers.get(a)!.map(u => (
                                                    <UserRow key={u.id} user={u} />
                                                ))}
                                            </>
                                        ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

                {this.appStore.isMobile && (
                    <div className="row pt-3">
                        {this.users.map(u => (
                            <div className="col-sm-6 mb-3" key={u.userName}>
                                <UserCard user={u} />
                            </div>
                        ))}
                    </div>
                )}

                <NothingHere
                    empty={!this.users.length}
                    icon={<BsPeopleFill />}
                    loading={this.loading}
                    label="No Users Available"
                    loadingMessage="Loading Users..."
                />

                <InviteModal modalHandle={this.modalHandle} />
            </div>
        );
    }

    @bind
    @action
    private openModal() {
        this.modalHandle.open();
    }

    @bind
    @action
    private async loadDataAsync() {
        this.loading = true;
        const userRoleView = this.props.match.params.userRoleView;
        let role: string | undefined;
        switch (userRoleView) {
            default:
            case UserRoleViews.Portal:
                role = 'PortalUser,PortalAdmin';
                break;
            case UserRoleViews.Production:
                role = 'ProdTestUser,ProdUser';
                break;
            case UserRoleViews.Admin:
                role = 'MriAdmin';
                break;
            case UserRoleViews.All:
                role = undefined;
                break;
        }

        try {
            this.usersStore.users.clear();

            await this.usersStore.loadUsersAsync({ params: { roleFilter: role } });
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    @action
    private async onSearchInputChange(event: React.FormEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;
        this.searchValue = target.value;
    }

    @bind
    @action
    private async onSortChange(value: string[]) {
        this.sort = value;
    }

    @computed
    private get users() {
        let users = toArray(this.usersStore.users);

        if (this.searchValue)
            users = users!.filter(u => {
                const s = { user: u, accounts: u.accounts.map(a => this.accountsStore.accounts.get(a)) };
                return deepStringFind(s, this.searchValue);
            });
        if (this.sort.length) {
            for (const s of this.sort) {
                const [k, d] = s.split(' ');

                users = users.sort((a: any, b: any) => {
                    const v1 = a[k];
                    const v2 = b[k];
                    if (v1 < v2) return -1;
                    if (v1 > v2) return 1;
                    return 0;
                });
                if (d == 'desc') {
                    users = users.reverse();
                }
            }
        }

        return users;
    }

    @computed
    private get accountUsers() {
        const accountUsers = this.users.reduce((agg, user) => {
            if (user.accounts?.length) {
                for (const account of user.accounts) {
                    let arr = agg.get(account);
                    if (!arr) arr = [];

                    arr.push(user);
                    agg.set(account, arr);
                }
            } else if (user.roles.includes('MriAdmin')) {
                // We'll make a magic string of __ to dictate custom groupings for the UI
                let arr = agg.get('__MRI Admins');
                if (!arr) arr = [];

                arr.push(user);
                agg.set('__MRI Admins', arr);
            } else {
                // We'll make a magic string of __ to dictate custom groupings for the UI
                let arr = agg.get('__No Assigned Accounts');
                if (!arr) arr = [];

                arr.push(user);
                agg.set('__No Assigned Accounts', arr);
            }
            return agg;
        }, new Map<string, Mri.Web.Portal.Controllers.Account.UserModel[]>());

        return accountUsers;
    }

    private modalHandle = new ModalHandle();
    @observable
    private loading: boolean = false;
    @observable
    private sort: string[] = [];
    @observable
    private searchValue: string = '';
    @inject
    private usersStore!: UsersStore;
    @inject
    private appStore!: AppStore;
    @inject
    private accountsStore!: AccountsStore;
    @inject
    private accountStore!: AccountStore;
}
